import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { useAuthStore } from '@/stores/auth.store';

const BASE_URL = import.meta.env.VITE_API_URL?.trim()
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Retry automatique sur erreurs réseau ou 5xx (Railway restart, timeout…)
axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) ||
    (!!error.response && error.response.status >= 500),
  onRetry: (retryCount, error) => {
    console.warn(`[API] Retry ${retryCount} — ${error.config?.url} (${error.message})`);
  },
});

// Attach access token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh token on 401
let isRefreshing = false;
let failedQueue: { resolve: (v: string) => void; reject: (e: unknown) => void }[] = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
}

api.interceptors.response.use(
  res => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || !original || original._retry || /^\/auth\/(login|refresh|logout)/.test(original.url ?? '')) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    const forceLogout = () => {
      useAuthStore.getState().logout();
      window.location.replace('/login');
    };

    try {
      const renew = async () => {
        // Tabs share persisted credentials: serialize rotation and reload the latest pair.
        await useAuthStore.persist.rehydrate();
        const current = useAuthStore.getState();
        if (!current.refreshToken) throw new AxiosError('Session absente', 'SESSION_MISSING');
        const previous = String(original.headers.Authorization ?? '');
        if (current.accessToken && previous !== 'Bearer ' + current.accessToken) return current.accessToken;
        const { data } = await api.post('/auth/refresh', { refreshToken: current.refreshToken });
        current.setTokens(data.data.accessToken, data.data.refreshToken);
        return data.data.accessToken as string;
      };
      const newAccess = navigator.locks
        ? await navigator.locks.request('vaegassist-session-refresh', renew)
        : await renew();
      processQueue(null, newAccess);
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (err) {
      const status = (err as AxiosError).response?.status;
      // Only force logout on genuine auth failure (401). Transient errors (503, network) keep the session.
      if (!status || status === 401) {
        processQueue(err, null);
        forceLogout();
      } else {
        processQueue(err, null);
      }
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);
